import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { baseQueryWithStatusCheck } from '../baseQueryWithStatusCheck';

// =======================
// Types
// =======================

export interface WidgetPosition {
  /** Widget ID (matches component registry key) */
  widgetId: string;
  /** Unique instance ID */
  instanceId: string;
  /** X position in grid units */
  x: number;
  /** Y position in grid units */
  y: number;
  /** Width in grid units */
  w: number;
  /** Height in grid units */
  h: number;
  /** Widget-specific config */
  config?: Record<string, any>;
  /** For container widgets: child widget IDs */
  children?: WidgetPosition[];
  /** Parent container instanceId (if nested) */
  parentId?: string;
}

export interface DashboardConfig {
  id: string;
  name: string;
  orgId: string;
  pageId: string;
  cols: number;
  rowHeight: number;
  gap: number;
  widgets: WidgetPosition[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface SaveDashboardRequest {
  pageId: string;
  name: string;
  widgets: WidgetPosition[];
  cols?: number;
  rowHeight?: number;
  gap?: number;
}

export interface FetchDashboardParams {
  pageId: string;
}

// =======================
// API
// =======================

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: baseQueryWithStatusCheck,
  tagTypes: ['Dashboard'],
  endpoints: (builder) => ({
    /**
     * Fetch dashboard config for a page
     * GET /api/dashboards/:pageId
     */
    fetchDashboard: builder.query<DashboardConfig | null, FetchDashboardParams>({
      query: ({ pageId }) => ({
        url: `/dashboards/${pageId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { pageId }) => [{ type: 'Dashboard', id: pageId }],
    }),

    /**
     * Save dashboard config
     * POST /api/dashboards
     */
    saveDashboard: builder.mutation<DashboardConfig, SaveDashboardRequest>({
      query: (body) => ({
        url: '/dashboards',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { pageId }) => [{ type: 'Dashboard', id: pageId }],
    }),

    /**
     * Update dashboard config
     * PUT /api/dashboards/:id
     */
    updateDashboard: builder.mutation<DashboardConfig, { id: string } & Partial<SaveDashboardRequest>>({
      query: ({ id, ...body }) => ({
        url: `/dashboards/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: 'Dashboard', id: result.pageId }] : []),
    }),

    /**
     * Delete dashboard
     * DELETE /api/dashboards/:id
     */
    deleteDashboard: builder.mutation<void, { id: string; pageId: string }>({
      query: ({ id }) => ({
        url: `/dashboards/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { pageId }) => [{ type: 'Dashboard', id: pageId }],
    }),

    /**
     * Fetch all dashboards for current org
     * GET /api/dashboards
     */
    fetchAllDashboards: builder.query<DashboardConfig[], void>({
      query: () => ({
        url: '/dashboards',
        method: 'GET',
      }),
      providesTags: ['Dashboard'],
    }),
  }),
});

export const {
  useFetchDashboardQuery,
  useSaveDashboardMutation,
  useUpdateDashboardMutation,
  useDeleteDashboardMutation,
  useFetchAllDashboardsQuery,
} = dashboardApi;
